import { exposureApi } from '@/helpers/api'
import { defineStore } from 'pinia'

interface IExposureResponse {
  show?: boolean
}

/**
 * Remote on/off switches for features, read from `/api/maplestory/exposures/v1/no-auth/{id}`.
 *
 * Fails closed. The API already answers `false` for an unknown, inactive or 0% exposure, and a
 * request that never lands is treated the same way: a feature that is meant to be off must not
 * appear because a lookup timed out. The cost is the reverse — a flag that is on stays hidden
 * during an outage — which is the safer of the two for a feature being rolled out.
 */
export const useExposureStore = defineStore('Exposure', () => {
  /** Settled answers, by exposure id. Absent means "not resolved yet". */
  const shown = ref<Record<string, boolean>>({})
  /** Dedupes concurrent lookups of the same id (the nav gate and the route guard both ask). */
  const pending = new Map<string, Promise<boolean>>()

  /** Resolves the flag once and caches it for the rest of the page's life. */
  const resolveExposure = (id: string): Promise<boolean> => {
    if (id in shown.value) return Promise.resolve(shown.value[id])

    const pendingRequest = pending.get(id)
    if (pendingRequest) return pendingRequest

    const request = exposureApi
      .get<IExposureResponse>(`/${id}`)
      .then((response) => response.data?.show === true)
      .catch(() => false)
      .then((show) => {
        shown.value[id] = show
        pending.delete(id)
        return show
      })

    pending.set(id, request)
    return request
  }

  /** Synchronous read for templates. `false` until the lookup settles. */
  const isExposed = (id: string) => shown.value[id] === true

  return { shown, resolveExposure, isExposed }
})
